import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TextView from "../../../TextView";
import ImageModal from "../../../ImageModal";
import { colors } from "../../../../utils";
import { useLocation } from "react-router-dom";
import useScreenSize from "../../../../hooks/useScreenSize";
import { CircularProgress } from "@mui/material";

const ImageContainer = styled.div`
  overflow: hidden;
  position: relative;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

const TextContainer = styled.div`
  background-color: #344054;
  padding: 8px;
  position: absolute;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IconsContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  gap: 10px;
  z-index: 1;
  background-color: ${({ showIcons }) =>
    showIcons ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)"};
  opacity: ${({ showIcons }) => (showIcons ? 1 : 0)};
  visibility: ${({ showIcons }) => (showIcons ? "visible" : "hidden")};
  transition: background-color 0.5s ease-in-out, opacity 0.5s ease-in-out,
    visibility 0.5s ease-in-out;
 
  ${ImageContainer}:hover & {
    opacity: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? 0 : 1};
    visibility: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? "hidden" : "visible"};
    background-color: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? "rgba(0, 0, 0, 0)" : "rgba(0, 0, 0, 0.5)"};
  }
`;

const IconView = styled.div`
  background-color: white;
  display: flex;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  align-items: center;

  &:hover .hidden-text {
    display: ${({ disableHover }) => (disableHover ? "none" : "block")};
  }
`;

const Text = styled(TextView)`
  color: ${colors.purple};
  margin-left: 4px;
  display: none;
`;

const LoadingDiv = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageBlock = ({
  src,
  title,
  showImageTitle,
  showHyperlink,
  hyperlink,
  style,
  imageStyle,
  isImageLoading,
  ...rest
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [showIcons, setShowIcons] = useState(false);

  const location = useLocation();
  const isEditPage = location.pathname.includes("builder");
  const isMobileScreen = location.pathname.includes("wheel/segment");
  const { isMobilePortrait, isMobileLandscape } = useScreenSize();
  const iconRef = useRef();
  const imgRef = useRef(); // Ref for the img element

  const disableHover = isMobilePortrait || isMobileLandscape;

  const isGiphyOrGif =
    src && (src.includes("giphy.com") || src.toLowerCase().includes(".gif"));

  const [imageLoaded, setImageLoaded] = useState(!isGiphyOrGif);

  // Ref to track if it's the first render
  const isFirstRender = useRef(true);

  // Reset imageLoaded when src changes, skip on first mount
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isGiphyOrGif) {
      setImageLoaded(false);
    } else {
      setImageLoaded(true);
    }
  }, [src, isGiphyOrGif]);

  // Attach event listeners manually using imgRef
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleLoad = () => {
      if (isGiphyOrGif) {
        console.log("Giphy or GIF image loaded:", src);
        setImageLoaded(true);
      }
    };

    const handleError = () => {
      if (isGiphyOrGif) {
        console.log("Failed to load Giphy or GIF image:", src);
        setImageLoaded(true);
      }
    };

    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [src, isGiphyOrGif]);

  const onOpenLink = () => {
    if (showHyperlink && hyperlink) {
      const url =
        hyperlink.startsWith("http://") || hyperlink.startsWith("https://")
          ? hyperlink
          : `https://${hyperlink}`;

      window.open(url, "_blank");
    }
  };

  const onOpenImage = () => {
    setOpenModal(true);
  };

  const toggleIconsVisibility = () => {
    if (disableHover) {
      console.log({ showIcons });
      setShowIcons(!showIcons);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconRef.current && !iconRef.current.contains(event.target)) {
        setShowIcons(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [iconRef]);

  // Optionally, force image reload by appending a unique query parameter
  const uniqueSrc = isGiphyOrGif ? `${src}?t=${Date.now()}` : src;

  return (
    <ImageContainer style={style}>
      {/* Display icons for image actions, except in edit mode */}
      {!isEditPage && !isMobileScreen && (
        <IconsContainer
          onClick={toggleIconsVisibility}
          ref={iconRef}
          disableHover={disableHover}
          showIcons={showIcons}
        >
          <IconView onClick={onOpenImage} disableHover={disableHover}>
            <VisibilityIcon style={{ color: colors.purple }} />

            {/* <Text className="hidden-text" variant="tag">
              View Image
            </Text> */}
          </IconView>
          {showHyperlink && (
            <IconView onClick={onOpenLink} disableHover={disableHover}>
              <OpenInNewIcon style={{ color: colors.purple }} />
              {/* <Text className="hidden-text" variant="tag">
                Open Link
              </Text> */}
            </IconView>
          )}
        </IconsContainer>
      )}

      {((isImageLoading && !isGiphyOrGif) ||
        (isGiphyOrGif && !imageLoaded)) && (
        <LoadingDiv>
          <CircularProgress />
        </LoadingDiv>
      )}

      <img
        ref={imgRef}
        key={uniqueSrc} // Use uniqueSrc to force reload if necessary
        src={uniqueSrc}
        alt={title}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display:
            (isImageLoading && !isGiphyOrGif) || (isGiphyOrGif && !imageLoaded)
              ? "none"
              : "block",
          ...imageStyle,
        }}
        onClick={toggleIconsVisibility}
        {...rest}
      />
      {/* Display image title if applicable */}
      {showImageTitle && title && (
        <TextContainer>
          <TextView
            variant="largeText"
            style={{ color: colors.white, fontWeight: 500 }}
          >
            {title}
          </TextView>
        </TextContainer>
      )}
      {/* Modal to view image in full size */}
      <ImageModal
        showTitle={false}
        open={openModal}
        handleClose={() => setOpenModal(false)}
        images={[{ src, title }]}
      />
    </ImageContainer>
  );
};

export default ImageBlock;
